import { Theme, CSSObject, styled } from '@mui/material/styles';
import MuiDrawer from '@mui/material/Drawer';
import { ListItemButton, ListItemIcon, ListItemText } from '@mui/material';

// Width of the sidebar when fully open (in pixels). Reduced for a slimmer layout.
const drawerWidth = 240;

const openedMixin = (theme: Theme): CSSObject => ({
  width: drawerWidth,
  transition: theme.transitions.create('width', {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.enteringScreen,
  }),
  overflowX: 'hidden',
});

const closedMixin = (theme: Theme): CSSObject => ({
  transition: theme.transitions.create('width', {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.leavingScreen,
  }),
  overflowX: 'hidden',
  width: `calc(${theme.spacing(7)} + 1px)`,
});

export const Drawer = styled(MuiDrawer)<{ open: boolean }>`
  flex-shrink: 0;
  white-space: nowrap;
  box-sizing: border-box;
  ${(props) => (props.open ? openedMixin(props.theme) : closedMixin(props.theme))}

  & .MuiDrawer-paper {
    ${(props) => (props.open ? openedMixin(props.theme) : closedMixin(props.theme))}
    border-right: 1px solid ${({ theme }) => theme.palette.grey[200]};

    /* The paper scrolls when the nav outgrows the viewport (e.g. Settings expanded
       on a laptop). Keep it scrollable but hide the scrollbar — a native scrollbar
       eats a third of the 57px collapsed rail. Same pattern as the note lists. */
    overflow-y: auto;
    scrollbar-width: none;
    &::-webkit-scrollbar {
      display: none;
    }
  }
`;

export const StyledListItemButton = styled(ListItemButton, {
  shouldForwardProp: (prop) =>
    prop !== 'isActive' && prop !== 'isOpen' && prop !== 'isCollapsed' && prop !== 'depth' && prop !== 'dense',
})<{ isActive: boolean; isOpen: boolean; isCollapsed?: boolean; depth?: number; dense?: boolean }>`
  color: ${({ isActive, theme }) => (isActive ? theme.palette.primary.main : theme.palette.grey[900])};
  padding-left: ${({ isOpen, depth }) => (isOpen ? `${(depth ?? 0) * 12}px` : '4px')};
  padding-right: ${({ isOpen }) => (isOpen ? '8px' : '4px')};
  height: ${({ isCollapsed, dense }) => (isCollapsed ? '32px' : dense ? '34px' : '40px')};
  transition: padding 0.3s;

  svg {
    color: ${({ isActive, isCollapsed, theme }) =>
      isActive ? theme.palette.primary.main : isCollapsed ? '#767676' : theme.palette.grey[900]};
  }

  &:hover {
    background-color: transparent;
    color: ${({ theme }) => theme.palette.primary.main};
    svg {
      color: ${({ theme }) => theme.palette.primary.main};
    }
  }
`;

export const StyledListItemIcon = styled(ListItemIcon, {
  shouldForwardProp: (prop) => prop !== 'isOpen',
})<{ isOpen: boolean }>`
  min-width: fit-content;
  padding-right: ${({ isOpen }) => (isOpen ? '12px' : '16px')};
  transition: padding-right 0.5s;
`;

export const Indicator = styled('div')`
  position: absolute;
  width: 3px;
  height: 38px;
  background-color: ${({ theme }) => theme.palette.primary.main};
  left: 8px;
  border-radius: 100px;
`;

export const StyledListItemText = styled(ListItemText)`
  span {
    /* Reduced font size and weight for sidebar navigation items */
    font-size: 16px;
    font-weight: 500;
    line-height: 24px;
    letter-spacing: 0.1px;
  }
`;

export const SectionContainer = styled('div', {
  shouldForwardProp: (prop) => prop !== 'isOpen' && prop !== 'isExpanded',
})<{ isOpen: boolean; isExpanded: boolean }>`
  background-color: ${({ isOpen, isExpanded }) => (isOpen && isExpanded ? '#F5F5F5' : 'transparent')};
  margin: ${({ isOpen, isExpanded }) => (isOpen && isExpanded ? '4px 14px' : '0')};
  border-radius: ${({ isOpen, isExpanded }) => (isOpen && isExpanded ? '10px' : '0')};
  transition:
    background-color 0.2s,
    margin 0.2s,
    border-radius 0.2s;

  & > .MuiListItem-root {
    padding-bottom: ${({ isExpanded }) => (isExpanded ? '0' : undefined)};
  }
`;

export const SubMenuContainer = styled('div', {
  shouldForwardProp: (prop) => prop !== 'isOpen',
})<{ isOpen: boolean }>`
  margin: ${({ isOpen }) => (isOpen ? '0 0 2px 16px' : '0')};
  padding: ${({ isOpen }) => (isOpen ? '0 0 4px 8px' : '0')};
  border-radius: 0 6px 6px 0;

  ${({ isOpen }) =>
    isOpen &&
    `
    & .MuiListItemIcon-root {
      padding-right: 8px;
    }

    & > .MuiListItem-root {
      position: relative;
      padding-top: 0;
      padding-bottom: 0;
      padding-right: 4px;

      &::after {
        content: '';
        position: absolute;
        left: -8px;
        top: 0;
        width: 1.5px;
        height: 100%;
        background-color: #767676;
      }

      &:last-child::after {
        height: 50%;
      }

      &::before {
        content: '';
        position: absolute;
        left: -8px;
        top: 50%;
        width: 12px;
        height: 1.5px;
        background-color: #767676;
      }
    }
  `}
`;

export const SubMenuListItemText = styled(ListItemText)`
  span {
    font-size: 14px;
    font-weight: 400;
    line-height: 22px;
    letter-spacing: 0.1px;
  }
`;
